import * as React from 'react';
import { api, getErrorMessage } from '@/api/api';
import { useToast } from '@/context/ToastContext';
import { useSurveyMetadata } from './useSurveyMetadata';
import type {
  Survey,
  SurveyQuestion,
  SurveyAnswer,
  SubmitSurveyRequest,
  SingleChoiceAnswer,
  RankedAnswer,
  MultiSelectAnswer,
  OpenTextAnswer,
  TripleResponseAnswer,
} from '@/types/survey';
import type {
  PersonalityTestAnswer,
  EchoDraftResponse,
  PartialPersonalityTestRequest,
} from '@/types/echo_creation';

/**
 * Personality Test hook (Survey v4 integration)
 * - Loads active survey
 * - Manages linear navigation across questions
 * - Maps legacy simple selection to strict SurveyAnswer types
 * - Submits responses via surveys API
 */
export const usePersonalityTest = (
  draftId: string,
  onComplete?: () => void,
  initialDraft?: EchoDraftResponse | null
) => {
  const { addToast } = useToast();

  const [survey, setSurvey] = React.useState<Survey | null>(null);
  const [currentIdx, setCurrentIdx] = React.useState(0);
  const [answers, setAnswers] = React.useState<Map<string, SurveyAnswer>>(new Map());
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);

  // Integrate Metadata Tracking Hook
  const { startQuestionTimer, stopQuestionTimer, trackAnswerChange, getFinalMetadata } =
    useSurveyMetadata();

  // Track previous index to handle timer stops correctly
  const prevIdxRef = React.useRef<number>(0);

  // Load Active Survey with AbortController to prevent double-fetch issues
  React.useEffect(() => {
    const controller = new AbortController();
    let isMounted = true;

    const loadSurvey = async () => {
      setIsLoading(true);
      try {
        const data = await api.surveys.getActiveSurvey(controller.signal);
        if (isMounted) {
          setSurvey(data);
          // Start timer for the first question
          startQuestionTimer();
        }
      } catch {
        if (isMounted) {
          addToast({
            title: 'Survey Error',
            subtitle: 'Could not load personality test questions.',
            type: 'error',
          });
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadSurvey();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [addToast, startQuestionTimer]);

  // Flatten questions from sections for linear navigation
  // Memoize allQuestions to keep the dependency array stable for hydration React.useEffect
  const allQuestions: SurveyQuestion[] = React.useMemo(
    () => survey?.sections.flatMap((section) => section.questions) ?? [],
    [survey]
  );

  const currentQuestion: SurveyQuestion | null = allQuestions[currentIdx] ?? null;
  const totalQuestions = allQuestions.length;

  // --- HYDRATE FROM DRAFT (resume answers and index) ---
  React.useEffect(() => {
    if (!survey || !initialDraft?.personality_test_results) return;

    const savedResults = initialDraft.personality_test_results as {
      answers?: PersonalityTestAnswer[];
    };
    if (!savedResults.answers || !Array.isArray(savedResults.answers)) return;

    const restored = new Map<string, SurveyAnswer>();

    savedResults.answers.forEach((saved) => {
      const question = allQuestions.find((qs) => qs.id === saved.question_id);
      if (!question) return;

      let parsed: SurveyAnswer | null = null;
      try {
        switch (question.type) {
          case 'single_choice': {
            const options = question.options || [];
            const opt = options[saved.answer_index];
            if (opt) {
              parsed = {
                question_id: question.id,
                question_type: 'single_choice',
                answer_id: opt.id,
                answer_text: opt.text,
              } as SingleChoiceAnswer;
            }
            break;
          }
          case 'ranked': {
            const ranking = JSON.parse(saved.answer_text || '[]');
            parsed = {
              question_id: question.id,
              question_type: 'ranked',
              ranking,
            } as RankedAnswer;
            break;
          }
          case 'multi_select': {
            const selected_options = JSON.parse(saved.answer_text || '[]');
            parsed = {
              question_id: question.id,
              question_type: 'multi_select',
              selected_options,
            } as MultiSelectAnswer;
            break;
          }
          case 'open_text': {
            parsed = {
              question_id: question.id,
              question_type: 'open_text',
              answer_text: saved.answer_text || '',
            } as OpenTextAnswer;
            break;
          }
          case 'triple_response': {
            const sub_answers = JSON.parse(saved.answer_text || '[]');
            parsed = {
              question_id: question.id,
              question_type: 'triple_response',
              sub_answers,
            } as TripleResponseAnswer;
            break;
          }
          default:
            break;
        }

        if (parsed) restored.set(question.id, parsed);
      } catch {
        // Ignore parsing errors for individual answers
      }
    });

    setAnswers(restored);
    // Resume at first unanswered question, or last if all answered
    const firstUnanswered = allQuestions.findIndex((qs) => !restored.has(qs.id));
    if (firstUnanswered !== -1) {
      setCurrentIdx(firstUnanswered);
    } else if (restored.size > 0 && restored.size === allQuestions.length) {
      setCurrentIdx(allQuestions.length - 1);
    }
  }, [survey, initialDraft, allQuestions]);

  // Metadata Timer Logic on Navigation
  React.useEffect(() => {
    // When index changes, stop timer for the OLD question index
    stopQuestionTimer(prevIdxRef.current);
    // Start timer for the NEW question index
    startQuestionTimer();
    // Update ref
    prevIdxRef.current = currentIdx;
  }, [currentIdx, startQuestionTimer, stopQuestionTimer]);

  /**
   * Generic answer handler that accepts a fully-typed SurveyAnswer object.
   * This supports all question types (ranked, single_choice, multi_select, open_text, triple_response).
   */
  const handleAnswer = React.useCallback(
    (answer: SurveyAnswer) => {
      setAnswers((prev) => {
        const hasExisting = prev.has(answer.question_id);
        trackAnswerChange(answer.question_id, hasExisting);

        const next = new Map(prev);
        next.set(answer.question_id, answer);
        return next;
      });
    },
    [trackAnswerChange]
  );

  const handlePrevious = () => {
    if (currentIdx > 0) setCurrentIdx((prev) => prev - 1);
  };

  /**
   * Helper to format answers for Echo API
   */
  const formatAnswersForApi = (
    answersToFormat: Map<string, SurveyAnswer>
  ): PersonalityTestAnswer[] =>
    Array.from(answersToFormat.values()).map((ans) => {
      const question = allQuestions.find((qq) => qq.id === ans.question_id);

      if (ans.question_type === 'single_choice') {
        const optionId = (ans as SingleChoiceAnswer).answer_id;
        const idx = (question?.options ?? []).findIndex((opt) => opt.id === optionId);
        const opt = (question?.options ?? [])[idx];
        return {
          question_id: ans.question_id,
          answer_index: idx >= 0 ? idx : 0,
          answer_text: opt?.text ?? optionId,
        };
      }

      let text = '';
      switch (ans.question_type) {
        case 'ranked':
          text = JSON.stringify((ans as RankedAnswer).ranking);
          break;
        case 'multi_select':
          text = JSON.stringify((ans as MultiSelectAnswer).selected_options);
          break;
        case 'open_text':
          text = (ans as OpenTextAnswer).answer_text;
          break;
        case 'triple_response':
          text = JSON.stringify((ans as TripleResponseAnswer).sub_answers);
          break;
        default:
          text = JSON.stringify(ans as unknown);
      }

      return {
        question_id: ans.question_id,
        answer_index: 0,
        answer_text: text,
      };
    });

  /**
   * Persist current progress to backend using the new Partial Save endpoint.
   * This is the fix for Phase 3 Action 1. Returns boolean indicating success.
   */
  const saveProgress = async (): Promise<boolean> => {
    if (!survey || answers.size === 0) return false;

    setIsSubmitting(true);
    try {
      const echoApiAnswers = formatAnswersForApi(answers);
      const payload: PartialPersonalityTestRequest = {
        test_version: survey.survey_version,
        answers: echoApiAnswers,
      };

      await api.echoes.savePartialPersonalityTest(draftId, payload);

      addToast({
        title: 'Progress Saved',
        subtitle: `${answers.size} answers saved.`,
        type: 'success',
      });
      return true;
    } catch (error) {
      const msg = getErrorMessage(error);
      addToast({
        title: 'Save Failed',
        subtitle: msg,
        type: 'error',
      });
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSave = async () => {
    await saveProgress();
  };

  const handleNext = async () => {
    if (!currentQuestion) return;

    if (currentQuestion.required && !answers.has(currentQuestion.id)) {
      addToast({
        title: 'Selection Required',
        subtitle: 'Please answer the question to proceed.',
        type: 'info',
      });
      return;
    }

    // Advance to next question
    if (currentIdx < totalQuestions - 1) {
      setCurrentIdx((prev) => prev + 1);
      return;
    }

    // Submit all answers at the end
    if (!survey) return;
    setIsSubmitting(true);
    try {
      // Ensure the final question timer is stopped before generating metadata
      stopQuestionTimer(currentIdx);
      const metadata = getFinalMetadata(totalQuestions);

      const payload: SubmitSurveyRequest = {
        survey_id: survey.id,
        answers: Array.from(answers.values()),
        is_completed: true,
        metadata,
      };

      // 1) Submit to Survey API (analytics/storage)
      await api.surveys.submitSurvey(payload);

      // 2) Update Echo Draft status via Echo Creation API
      const echoApiAnswers = formatAnswersForApi(answers);

      await api.echoes.submitPersonalityTest(draftId, {
        test_version: survey.survey_version,
        answers: echoApiAnswers,
      });

      addToast({
        title: 'Assessment Complete',
        subtitle: 'Personality profile analyzed and saved to draft.',
        type: 'success',
      });
      onComplete?.();
    } catch (error) {
      addToast({
        title: 'Submission Failed',
        subtitle: getErrorMessage(error),
        type: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    // navigation
    currentIdx,
    currentQuestion,
    totalQuestions,
    // answers (Map to allow direct get by question_id)
    answers,
    // actions
    handleAnswer,
    handleNext,
    handlePrevious,
    handleSave,
    saveProgress,
    // status
    isSubmitting,
    isLoading,
  };
};

export default usePersonalityTest;
